/**
 * Everything the chat sidebar needs from Heidi Work, resolved by the app shell.
 *
 * `ChatSidebar` renders on four surfaces and used to reach into the Work,
 * Routines and inbox trees for itself. It no longer does: the shell owns those
 * trees, already composes the sidebar's navigation handlers here
 * (`useChatSidebarHandlers`), and now supplies the Work half as one value.
 *
 * Every consumer passes the bindings, so nothing about the rendered sidebar
 * changes — what changes is who imports Work.
 */

import { pendingNavHrefAtom } from '@/atoms/sideNavAtoms';
import { useDeleteProject } from '@/features/projects/hooks';
import {
  RoutinesSidebarNavEntry,
  useRoutinesSpotlightSidebarLock,
  WorkflowsSidebarSection,
} from '@/features/workflows/components/WorkflowsSidebarSection';
import { triggerMakeAsWorkflow } from '@/features/workflows/triggerMakeAsWorkflow';
import {
  filterChatsForWorkAvailability,
  getChatHref,
  getChatListVersion,
  isWorkChatLandingPath,
  WORK_CHAT_HOME_PATH,
  workHomeHref,
  type ChatRoutingFields,
} from '@heidi/agents/contracts';
import {
  currentWorkflowSelectionAtom,
  currentWorkHomeViewAtom,
  currentWorkModeAtom,
  useProjectIdsNeedingAttention,
  workflowBrowseResetAtom,
} from '@heidi/agents/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useMemo, type ReactNode } from 'react';
import { isWorkHomeEvidenceMode } from './isWorkHomeEvidenceMode';
import { useOpenEvidenceHome } from './useOpenEvidenceHome';

// Imported, not re-declared: `/scribe/chat/projects` is a key by shape, and
// `key-single-site` refuses a second declaration of it — the route already has one
// site, in the `@heidi/agents` contracts door. The local alias stays because it is
// what the callbacks below read, and renaming five call sites to prove a point
// about a name is not a change worth making.
const WORK_HOME_HREF = WORK_CHAT_HOME_PATH;

export interface ChatSidebarWorkBindings {
  /** Hide harness rows the Work gate cannot open. */
  filterChatsForWorkAvailability: <T extends ChatRoutingFields>(
    chats: T[],
    isWorkEnabled: boolean
  ) => T[];
  /** Detail URL for a chat row — v1 and v2 rows live on different routes. */
  getChatHref: (chat: ChatRoutingFields) => string;
  /** `1` = legacy Evidence chat, `2` = harness-backed project chat. */
  getChatListVersion: (chat: ChatRoutingFields) => 1 | 2;
  /** `DELETE /projects/:id`, the v2 leg of the bulk-delete fan-out. */
  deleteProject: (projectId: string) => Promise<unknown>;
  isDeletingProject: boolean;
  /** Chat ids with an inbox item waiting on the user. */
  attentionChatIds: Set<string>;
  /** Promote a v2 chat to a routine and open it. */
  makeAsWorkflow: (chatId: string) => void;
  /** The Routines spotlight pins the sidebar open while the tour runs. */
  isSidebarLockedOpen: boolean;
  /** The Routines row owns the highlight — unless a routine itself is open. */
  isRoutinesNavActive: boolean;
  /** Work home is currently showing Evidence mode. */
  isWorkHomeEvidenceMode: boolean;
  /** Open the Work home's routines split view. */
  openRoutines: () => void;
  /** Open the Work home with the composer Evidence pill selected. */
  openEvidenceHome: () => void;
  /** Spotlight anchor wrapper for the Routines nav row. */
  renderRoutinesNavEntry: (props: {
    isSidebarOpen: boolean;
    onRequestOpenSidebar: () => void;
    children: ReactNode;
  }) => ReactNode;
  /** The scheduled + on-demand routine sections above the chat list. */
  renderRoutineSections: (props: {
    isSidebarPanelEnabled: boolean;
    currentChatId?: string | null;
  }) => ReactNode;
}

export function useChatSidebarWorkBindings(): ChatSidebarWorkBindings {
  const router = useRouter();
  const setPendingNavHref = useSetAtom(pendingNavHrefAtom);
  const currentWorkHomeView = useAtomValue(currentWorkHomeViewAtom);
  const currentWorkMode = useAtomValue(currentWorkModeAtom);
  const currentWorkflowSelection = useAtomValue(currentWorkflowSelectionAtom);
  const bumpWorkflowBrowseReset = useSetAtom(workflowBrowseResetAtom);
  const isSidebarLockedOpen = useRoutinesSpotlightSidebarLock();
  const openEvidenceHome = useOpenEvidenceHome();
  const attentionChatIds = useProjectIdsNeedingAttention();
  const { mutateAsync: deleteProjectAsync, isPending: isDeletingProject } =
    useDeleteProject();

  const openRoutines = useCallback(() => {
    // Already on the Work landing: a push that only repeats ?view=routines
    // fires no routeChangeComplete, so a pending write would stick and Home
    // would stay selected. Home → ?view=routines is a real query change; the
    // Routines highlight comes from currentWorkHomeView, not this pending write.
    if (!isWorkChatLandingPath(router.asPath)) {
      setPendingNavHref(WORK_HOME_HREF);
    }
    // Reset any open routine back to the browse surface — works even when
    // already on the routines page (a same-route push is a no-op).
    bumpWorkflowBrowseReset((n) => n + 1);
    void router.push(workHomeHref({ view: 'skills' }), undefined, { shallow: true });
  }, [bumpWorkflowBrowseReset, router, setPendingNavHref]);

  const makeAsWorkflow = useCallback(
    (chatId: string) => {
      triggerMakeAsWorkflow(chatId);
      void router.push(`${WORK_HOME_HREF}/${chatId}`);
    },
    [router]
  );

  const renderRoutinesNavEntry = useCallback(
    ({
      isSidebarOpen,
      onRequestOpenSidebar,
      children,
    }: {
      isSidebarOpen: boolean;
      onRequestOpenSidebar: () => void;
      children: ReactNode;
    }) => (
      <RoutinesSidebarNavEntry
        isSidebarOpen={isSidebarOpen}
        onRequestOpenSidebar={onRequestOpenSidebar}
      >
        {children}
      </RoutinesSidebarNavEntry>
    ),
    []
  );

  const renderRoutineSections = useCallback(
    ({
      isSidebarPanelEnabled,
      currentChatId,
    }: {
      isSidebarPanelEnabled: boolean;
      currentChatId?: string | null;
    }) => (
      <WorkflowsSidebarSection
        isSidebarPanelEnabled={isSidebarPanelEnabled}
        currentChatId={currentChatId}
        attentionProjectIds={attentionChatIds}
      />
    ),
    [attentionChatIds]
  );

  return useMemo(
    () => ({
      filterChatsForWorkAvailability,
      getChatHref,
      getChatListVersion,
      deleteProject: deleteProjectAsync,
      isDeletingProject,
      attentionChatIds,
      makeAsWorkflow,
      isSidebarLockedOpen,
      // Only the parent OR a specific routine is active at once: when a
      // routine is open the routine row owns the highlight, so the
      // "Routines" entry backs off.
      isRoutinesNavActive:
        currentWorkHomeView === 'skills' && currentWorkflowSelection == null,
      isWorkHomeEvidenceMode: isWorkHomeEvidenceMode(
        currentWorkHomeView,
        currentWorkMode
      ),
      openRoutines,
      openEvidenceHome,
      renderRoutinesNavEntry,
      renderRoutineSections,
    }),
    [
      attentionChatIds,
      currentWorkHomeView,
      currentWorkMode,
      currentWorkflowSelection,
      deleteProjectAsync,
      isDeletingProject,
      isSidebarLockedOpen,
      makeAsWorkflow,
      openEvidenceHome,
      openRoutines,
      renderRoutineSections,
      renderRoutinesNavEntry,
    ]
  );
}
